import { lazy } from 'react'
import { Lazy, isLazy, isSuspense } from 'react-is'
import _ from 'lodash'
import libs from '../../libs/lazy-ui'
import { mergeExposes } from '../../init/init'
import { importLibs } from '../../libs/base'
import { hasTsgSupport } from '../../framework/tsg'
import {
  inited,
  events,
  onAuthed,
  getFrameworkVar,
  getFrameworkVars,
  hasMicroApp,
  getOidcClientId,
  getFrameworkServiceUrl,
  isOidcClient,
} from '../../framework/states'
import { lazyImportWebComponent } from '../../framework/webComponent'
import {
  ASC_PORTAL_MICRO_APP,
  ASC_TIER_1_SUPPORT_ROLE,
  CN_REGION,
  FAWKES_APP_ID,
  FAWKES_MICRO_APP_NAME,
  MSP_PATH_PREFIX,
  MULTI_TENANT_PATH_PREFIX_REGEX,
  STRATA_CLIENT_ID,
  NETSEC_URL_KEY,
  RUNNING,
  SASE_CLIENT_ID,
  SASE_CN_CLIENT_ID,
  SASE_CN_URL_KEY,
  SASE_URL_KEY,
  SDWAN_APP_ID,
  SCM_CLIENT_ID,
  MSP_MICRO_APP_NAME,
} from '../../framework/constants'
import { showLoadingMask } from '../../framework/loadingMask'
import { getOverrideTsgView } from '../../framework/overrideInstances'
import { RedirectionViews } from '../../framework/enums'
import { createTimeoutPromise, ensureArray, isNonemptyArray } from '../../framework/utils'
import { __info, __isLocalhost } from '../../init/debug'
import type { ComponentType } from 'react'
import type { AuthState } from '../../framework/stateTypes'
import type {
  LazyFactory,
  AvailFunc,
  MicroAppExported,
  InitedMicroAppConfig,
  InitedMicroApp,
  NavItem,
  NavAction,
  NavAppAttr,
  NavParams,
  Exposes,
  MicroAppExportedOrPromise,
  StoreState,
  AvailParam,
  ComponentOrLazyFactory,
  NavItemArray,
  NavItemComponent,
} from '../../framework/microAppTypes'
import type {
  TsgUnloadFn,
  TsgLoadFn,
  TsgLoadFnParam,
  TsgUnloadFnParam,
  TsgUnloadResult,
} from '../../framework/newMicroAppTypes'
import type { AuthUpdate } from '../../framework/actions'

type MicroAppCfgOrPromise = InitedMicroApp | (Promise<InitedMicroApp> & NavAppAttr)
type NavigationCfg = InitedMicroApp[] & {
  pathAvailMap: Record<string, AvailFunc>
  flattenNavigationList: NavItem[]
  actions: NavAction[]
}
type MicroAppNameAndId = { appName?: string, microAppId?: string }

const isComp = (Comp: ComponentType): boolean => Boolean(
  Comp.displayName || Comp.propTypes || Comp.defaultProps || // eslint-disable-line react/forbid-foreign-prop-types
  Comp.prototype?.isReactComponent || Comp.prototype?.render
)
const isLazyOrSuspense = (Comp: ComponentType | LazyFactory): boolean => Boolean(
  isLazy(Comp) || isSuspense(Comp) || (Comp as { $$typeof?: symbol }).$$typeof === Lazy
)

export function hasExports<T = any>(exports: T): exports is NonNullable<T> {
  if (!exports) {
    return false
  }
  if (Array.isArray(exports)) {
    return exports.length > 0
  }
  for (const _ in exports) {
    return true
  }
  if (typeof ((exports as any).then || exports) === 'function') { // promise or function
    return true
  }
  return false
}

const autoWrapLazy = (Comp: ComponentType | LazyFactory | string | undefined, item: Partial<NavItem>) => {
  if (!Comp) {
    return
  }
  const lazyComp = item.lazy ?? (Comp as ComponentOrLazyFactory).lazy
  if (lazyComp === false || _.isString(Comp)) {
    return Comp
  }
  if (lazyComp === true ||
    (_.isFunction(Comp) && !isLazyOrSuspense(Comp) && !(Comp as { noLazy?: boolean }).noLazy && !isComp(Comp as ComponentType))) {
    // expecting `() => import(...)`
    const newComp: ComponentOrLazyFactory = lazy(Comp as LazyFactory)
    const { layout = item.layout } = Comp as ComponentOrLazyFactory
    if (layout) {
      newComp.layout = layout
    }
    return newComp
  }
  return Comp
}
const autoWrapLazyWithMicroAppAttr = (component: NavItemComponent | undefined, child: NavItem) => {
  if (!component) {
    return component
  }
  const { appName, microAppId } = (component as MicroAppNameAndId)
  const lazyComp = autoWrapLazy(component, child)
  return Object.defineProperties(lazyComp, {
    appName: { value: appName },
    microAppId: { value: microAppId },
  })
}
const traverseChildren = (ch: NavItem[]) => ch.map(({ children, component, ...child }: NavItem) => {
  if (component) {
    child.component = autoWrapLazyWithMicroAppAttr(component, child)
  }
  if (_.isFunction(children)) {
    children = children?.(child)
  }
  if (Array.isArray(children)) {
    child.children = traverseChildren(children)
  }
  return child
})
const normalizeNav = ({ featureAvil, children, component, navComponent, ...nav }: NavAction | NavItem): NavItem => ({
  className: 'parent-node',
  ...nav,
  children: children && traverseChildren(children),
  component: autoWrapLazyWithMicroAppAttr(component, nav),
  navComponent: autoWrapLazyWithMicroAppAttr(navComponent, nav),
})

const getFlattenNavigationList = (list: NavItem[]): NavItem[] => (
  list.reduce((r, { children, ...rest }) => {
    r.push(rest)
    if (children) r.push(...getFlattenNavigationList(children))
    return r
  }, [] as NavItem[])
)

const getPathAvailMap = (list: NavItem[]) => (
  list.reduce((map, { path, avail }) => {
    if (path) {
      map[path] = avail
    }
    return map
  }, {} as Record<string, AvailFunc>)
)

export const copyNavAppAttr = (target: any, source: NavAppAttr) => {
  if (!target || !source) {
    return target
  }
  const { id, appName, appVars, varName } = source as any
  target.id = id || appName
  target.appName = appName
  target.appVars = appVars
  if (varName) { // TODO: remove once format updated
    target.varName = varName
  }
}

export const resolveMicroApps = async (apps: MicroAppExportedOrPromise[]): Promise<InitedMicroApp[]> => {
  const initMicroApp = (app: MicroAppExportedOrPromise) => {
    if (!_.isFunction(app)) {
      return hasExports(app) ? app : undefined
    }
    const params = Object.assign(Object.create(libs), {
      id: app.id,
      name: app.appName,
      vars: app.appVars,
      importLibs,
      onAuthed,
      lazyImportWebComponent,
    }) as NavParams
    try {
      const navApp = app(params) as InitedMicroApp | null | undefined | Promise<InitedMicroApp | null | undefined> & NavAppAttr
      if (!hasExports(navApp)) {
        return undefined
      }
      copyNavAppAttr(navApp, app)
      const { exposes } = app as InitedMicroAppConfig
      const mergeExposesFromApp = (nav: any) => {
        if (nav && exposes) {
          let mergedExposes = { ...exposes, ...(nav as InitedMicroAppConfig).exposes }
          if (!mergedExposes.default && app.appName === FAWKES_MICRO_APP_NAME) {
            mergedExposes = { default: mergedExposes }
          }
          Object.assign(nav, { exposes: mergedExposes })
        }
        return nav
      }
      return (navApp as Promise<InitedMicroApp>)?.then?.((nav: InitedMicroApp) => {
        if (!hasExports(nav)) {
          return undefined
        }
        copyNavAppAttr(nav, navApp as NavAppAttr)
        mergeExposesFromApp(nav)
        return nav
      }, (error) => {
        console.error(`failed to init micro-app ${app.appName}`, error)
        return { id: navApp.id, appName: navApp.appName, error } as InitedMicroApp
      }) || mergeExposesFromApp(navApp) as InitedMicroApp
    }
    catch (error) {
      console.error(`failed to init micro-app ${app.id || app.appName}`, error)
      return { id: app.id, appName: app.appName, error } as InitedMicroApp
    }
  }
  const handleAppErr = (err: Error) => {
    console.error(`micro-app ${(err as any).app || ''} error:`, err)
  }
  const navApps = apps.map((app: MicroAppExportedOrPromise) => {
    const { initedApp: inited } = app as any
    if (inited) {
      return inited
    }
    const initedApp = (app as Promise<MicroAppExported>)?.then?.(initMicroApp, handleAppErr) || initMicroApp(app)
    Object.defineProperty(app, 'initedApp', { value: initedApp }) // ensure only init app once
    return initedApp
  }) as MicroAppCfgOrPromise[]
  const navAppsResolved = (
    navApps.some(app => (app as Promise<InitedMicroApp>)?.then) ? await Promise.all(navApps) : navApps
  ).filter(Boolean) as InitedMicroApp[]
  return navAppsResolved
}

const getRedirectionViewForTSG = (auth: AuthState | undefined): RedirectionViews => {
  if (!auth?.instances) {
    return RedirectionViews.DEFAULT
  }

  const getRedirectionViewFromInstances = () => {
    const paInst = auth.instances?.get(FAWKES_APP_ID)

    if (paInst?.status === RUNNING && (paInst.platform_region || paInst.region) === CN_REGION) {
      return RedirectionViews.SASE_CN
    }

    if (!paInst?.runtime_attributes || paInst.status !== RUNNING) {
      const sdwanInst = auth.instances?.get(SDWAN_APP_ID)
      if (sdwanInst?.status === RUNNING) {
        if (sdwanInst.runtime_attributes?.is_oneapp_ready) {
          return RedirectionViews.SASE_IA
        }
        else {
          return RedirectionViews.SASE
        }
      }

      return RedirectionViews.DEFAULT
    } // otherwise pa is running with runtime_attributes

    const isOneappReady = paInst?.runtime_attributes?.is_oneapp_ready
    if (!isOneappReady) {
      return RedirectionViews.SASE
    }

    return RedirectionViews.SASE_IA
  }

  return getOverrideTsgView(auth?.tsg_id) || getRedirectionViewFromInstances()
}

const handleRedirectionView = (view: RedirectionViews, { tsg_id, tsgs, access }: AuthState) => {
  const tsgId = tsg_id!
  const vars = getFrameworkVars()
  const curPath = window.location.pathname
  if (MULTI_TENANT_PATH_PREFIX_REGEX.test(curPath) ||
    __isLocalhost || vars.hub || vars.aiops_free || vars.tsg_auto_redirection === false) {
    return
  }
  if (curPath === '/') {
    if (hasMicroApp(ASC_PORTAL_MICRO_APP) && access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)) {
      return // which will redirect to /asc
    }
    if (tsgs?.hasParentOrSubTenants(tsgId) && hasMicroApp(MSP_MICRO_APP_NAME)) {
      return // which will redirect to /msp
    }
  }
  const scm_client_id = vars.netsec_client_id || SCM_CLIENT_ID
  if (view === RedirectionViews.SASE && isOidcClient(scm_client_id) && curPath === '/aiops') {
    return // skip redirect to sase for aiops
  }
  const curClientId = getOidcClientId()
  const expectedClientId = {
    [RedirectionViews.DEFAULT]: curClientId,
    [RedirectionViews.SASE]: SASE_CLIENT_ID,
    [RedirectionViews.SASE_IA]: scm_client_id,
    [RedirectionViews.HYBRID]: scm_client_id,
    [RedirectionViews.SASE_CN]: SASE_CN_CLIENT_ID,
  }[view]
  if (expectedClientId && !isOidcClient(expectedClientId)) {
    const urlKey = {
      [SASE_CLIENT_ID]: SASE_URL_KEY,
      [STRATA_CLIENT_ID]: NETSEC_URL_KEY,
      [SCM_CLIENT_ID]: NETSEC_URL_KEY,
      [scm_client_id]: NETSEC_URL_KEY,
      [SASE_CN_CLIENT_ID]: SASE_CN_URL_KEY,
    }[expectedClientId]!
    const baseUrl = getFrameworkServiceUrl(urlKey)
    if (baseUrl && baseUrl !== window.location.origin) {
      __info(`Redirecting to ${view} view client id ${expectedClientId} for TSG from client id ${curClientId}`)
      const { pathname, search, hash } = window.location
      const url = new URL(`${pathname}${search}${hash}`, baseUrl)
      url.searchParams.set('tsg_id', tsgId)
      showLoadingMask()
      window.location.href = url.toString()
      throw new Error('redirection')
    }
  }
}

const filterInitedMicroApps = (microApps: InitedMicroApp[], auth: AuthState | undefined): InitedMicroApp[] => {
  if (!getFrameworkVar('dynamic_filter_microapps_for_tsg') || getFrameworkVar('hub')) {
    return microApps
  }  // skip redirection for hub
  const redirectionView = getRedirectionViewForTSG(auth)
  if (auth?.tsg_id) {
    handleRedirectionView(redirectionView, auth)
    if (auth.instances?.flags) {
      auth.instances.flags.redirectionView = redirectionView
    }
  }
  const disabledMicroAppIds = ensureArray(getFrameworkVar<Record<string, string[] | undefined>>('disabled_microapps_for_tsg_view')?.[redirectionView])
  const disabledMicroAppNames = [] as string[]
  if (!auth?.instances?.isRunningStatus(FAWKES_APP_ID)) {
    const varValue = getFrameworkVar('disable_microapps_wo_fawkes')
    const microAppNames = isNonemptyArray(varValue) ? varValue as string[] : ['pai', 'dem', 'management-app']
    disabledMicroAppNames.push(...microAppNames)
    __info('Disable micro-apps for no running Fawkes:', disabledMicroAppNames)
  }
  if (!disabledMicroAppIds.length && !disabledMicroAppNames.length) {
    return microApps
  }
  const filteredOutMicroAppIds = []
  const filteredMicroApps = microApps.filter((microapp) => {
    const microAppName = microapp.appName!
    const microAppId = microapp.id || microAppName
    if (!disabledMicroAppIds.includes(microAppId) && !disabledMicroAppNames.includes(microAppName)) {
      return true
    }
    filteredOutMicroAppIds.push(microAppId)
    return false
  })
  __info(`Disabled micro-apps for ${redirectionView} view:`, disabledMicroAppIds)
  return filteredMicroApps
}

export const dynamicMicroAppsTsgLoad = ({ auth, main }: StoreState, initedMicroApps: InitedMicroApp[]): Promise<InitedMicroApp[]> => {
  if (!initedMicroApps.some(_.isFunction)) {
    return Promise.resolve(filterInitedMicroApps(initedMicroApps, auth)) // skip if no tsgLoad function defined
  }
  const fnParam = {
    auth,
    main,
  } as TsgLoadFnParam
  const param = {
    ...fnParam,
  } as TsgLoadFnParam
  const starts = Date.now()
  if (hasTsgSupport()) {
    const tsgUnloadFns = new Set<TsgUnloadFn>()
    param.onTsgUnload = (cb: TsgUnloadFn): void => {
      if (!_.isFunction(cb)) {
        console.error('callback function must be provided for onTsgUnload', cb)
        throw new Error('callback function must be provided for onTsgUnload')
      }
      tsgUnloadFns.add(cb)
    }
    events.once('switch-tsg-unload', (update: AuthUpdate) => {
      const tsgUnloadFnArray = Array.from(tsgUnloadFns)
      if (!tsgUnloadFnArray.length) {
        return
      }
      const param = { ...fnParam } as TsgUnloadFnParam
      update.waitPromise = Promise.all(tsgUnloadFnArray.map(async (fn) => {
        try {
          return await fn(param)
        }
        catch (error) {
          console.error('failed to run tsg unload callback', error)
        }
      })).then((results) => {
        const forceReload = results.some(res => res?.forceReload)
        return { forceReload } as TsgUnloadResult
      })
    })
  }
  else { // noop for non-TSG
    param.onTsgUnload = (cb: TsgUnloadFn) => { }
  }

  const convertExports = (result: any, original: NavAppAttr) => {
    if (!result) {
      const exposes = (original as InitedMicroAppConfig).exposes
      if (!hasExports(exposes)) {
        return undefined
      }
      result = { exposes }
    }
    const { actions, exposes } = result
    if (Array.isArray(result)) {
      result = {
        navigation: result
      }
    }
    else {
      const nav = result as NavItem
      if (nav.component || Array.isArray(nav.children)) {
        result = {
          navigation: [nav]
        }
      }
      else {
        result = { ...result }
      }
    }
    if (actions) {
      result.actions = Array.isArray(actions) ? actions : [actions]
    }
    const fnExposes = (original as InitedMicroAppConfig).exposes
    if (hasExports(exposes)) {
      result.exposes = fnExposes ? { ...fnExposes, ...exposes } : exposes
    }
    else if (hasExports(fnExposes)) {
      result.exposes = fnExposes
    }
    copyNavAppAttr(result, original)
    const { appName, id: microAppId = appName } = original
    const appProps = {
      appName: { value: appName },
      microAppId: { value: microAppId }
    }
    const traverseChildren = (ch: NavItem[]) => ch.map(({ children, navComponent, component, avail, ...child }: NavItem) => {
      if (component) {
        child.appName = appName
        child.microAppId = microAppId
        child.component = Object.defineProperties(component, appProps)
      }
      if (navComponent) {
        child.navComponent = Object.defineProperties(navComponent, appProps)
      }
      if (_.isFunction(children)) {
        child.children = () => {
          const generated = children?.(child)
          if (isNonemptyArray(generated)) {
            return traverseChildren(generated)
          }
        }
      }
      else if (isNonemptyArray(children)) {
        child.children = traverseChildren(children)
      }
      if (_.isFunction(avail)) {
        child.avail = Object.defineProperties(avail, appProps)
      }
      return child
    })
    if (isNonemptyArray(result.navigation)) {
      result.navigation = traverseChildren(result.navigation)
    }
    if (isNonemptyArray(result.actions)) {
      result.actions = traverseChildren(result.actions)
    }
    return result as InitedMicroApp
  }
  const runOnload = async (loadFn: TsgLoadFn): Promise<InitedMicroApp | undefined> => {
    const [timeoutPromise, clear] = createTimeoutPromise(30000, '30s timeout')
    try {
      const result = await Promise.race([
        loadFn(param),
        timeoutPromise,
      ]) as InitedMicroApp
      clear()
      if (!result) { // if tsg load fn return fasly value, even if it has static exports, skip it
        return undefined
      }
      return convertExports(result, loadFn as NavAppAttr)
    }
    catch (e) {
      clear()
      console.error('failed to execute tsg load function', (loadFn as any).id, e)
      return undefined
    }
  }
  const filteredInitedMicroApps = filterInitedMicroApps(initedMicroApps, auth)
  return Promise.all(filteredInitedMicroApps.map((microApp) => {
    if (_.isFunction(microApp)) {
      return runOnload(microApp as TsgLoadFn) as Promise<InitedMicroApp>
    }
    return convertExports(microApp, microApp)
  })).then(microApps => {
    const microAppMap = microApps.reduce((map, microApp) => {
      if (microApp) {
        const existing = map.get(microApp.appName)
        if (existing) {
          console.error(`ignore duplicated micro-app name ${microApp.appName} with different ids`, microApp, existing)
        }
        else {
          map.set(microApp.appName, microApp)
        }
      }
      return map
    }, new Map())
    const exported = Array.from(microAppMap.values())
    __info(`micro-apps exported after tsg init in ${Date.now() - starts}ms`, exported)
    return exported
  })
}

const ensureUseApprovedIcon = (microAppsNav: NavItem[]) => {
  const approvedIcons = microAppsNav.reduce((o, item) => {
    if (item?.icon?.__approved_icon) {
      if (item.key) {
        o[item.key] = item.icon
      }
      if (item.path?.startsWith('/')) {
        o[item.path] = item.icon
      }
    }
    return o
  }, {} as Record<string, any>)
  if (Object.keys(approvedIcons).length) {
    for (const item of microAppsNav) {
      if (item.icon && !item.icon.__approved_icon) {
        const replaceIcon = (item.key && approvedIcons[item.key]) ||
          (item.path?.startsWith('/') && approvedIcons[item.path])
        if (replaceIcon) {
          item.icon = replaceIcon
        }
      }
    }
  }
  return microAppsNav
}

export const processMicroApps = (navAppsResolved: InitedMicroApp[]) => {
  const microAppsNav = navAppsResolved.reduce((all, app) => {
    const { appName, appVars, error } = app as NavAppAttr & { error: Error }
    if (error) {
      return all
    }
    const assignAppVars = (app: NavItem) => Object.assign(app, { appName, appVars })
    const pushEach = (app: NavItem) => {
      app = assignAppVars(app)
      if (app.hoistChildren && Array.isArray(app.children)) {
        const { children, ...nav } = app
        all.push(nav, ...children.map(assignAppVars))
      }
      else {
        all.push(app)
      }
    }
    if ((app as InitedMicroAppConfig).navigation) {
      app = (app as InitedMicroAppConfig).navigation as InitedMicroApp
    }
    if (_.isFunction(app)) {
      app = app?.() || app
    }
    if (Array.isArray(app)) {
      (app as NavItem[]).forEach(pushEach)
    }
    else if (app) {
      const nav = app as NavItem
      if (nav.component || Array.isArray(nav.children)) {
        pushEach(app as NavItem)
      }
      else if (!nav.path && !nav.exposes && !nav.actions) {
        console.warn(`invalid nav item exposed from ${app.appName}`, nav)
      } // ignore object with only exposes
    }
    return all
  }, [] as NavItem[])
  const mergeAvail = (target: NavItem, source: NavItem): { avail?: AvailFunc } => {
    if ((!source.avail || _.isBoolean(source.avail)) && (!target.avail || _.isBoolean(target.avail))) {
      return {}
    }
    const getAvailAndApplyFlag = (item: NavItem): AvailFunc | undefined => {
      if (_.isBoolean(item.avail)) { // deal with some mistakes in code
        console.error('navitem.avail should be a function intead of boolean value', item)
        return undefined
      }
      if (!item.avail || !(item.callAvail || item.asPlaceholder || item.isSetup)) {
        return item.avail
      }
      if (item.avail.mergeAvail) {
        return item.avail
      }
      return Object.assign(item.avail, {
        mergeAvail: (_.isString(item.callAvail) && item.callAvail) ||
          (item.asPlaceholder && 'append') || 'auto'
      })
    }
    const tarAvail = getAvailAndApplyFlag(target)
    const srcAvail = getAvailAndApplyFlag(source)
    if (!srcAvail || tarAvail?.mergeAvail === 'override') {
      return { avail: tarAvail }
    }
    if (!tarAvail || srcAvail?.mergeAvail === 'override') {
      return { avail: srcAvail }
    }
    else if (tarAvail.mergeAvail === 'append' || srcAvail.mergeAvail === 'append') {
      const avail: AvailFunc = (...args: [AvailParam]) => tarAvail(...args) && srcAvail(...args)
      Object.assign(avail, { mergeAvail: 'append', _avails_: [srcAvail, tarAvail] })
      return { avail }
    }
    else if (source.component && !target.component) {
      return { avail: srcAvail }
    }
    else if (target.component && !source.component) {
      return { avail: tarAvail }
    }
    return {}
  }
  const mergeChildren = (children1?: NavItem[], children2?: NavItem[]): NavItem[] | undefined => {
    if (!children1?.length) {
      return children2 || children1 || undefined
    }
    if (!children2?.length) {
      return children1 || children2 || undefined
    }
    const merged = children1.concat(children2).filter(Boolean)
    return merged.length ? mergeNav(merged, true) : merged
  }
  const mergeNav = (navItems: NavItem[], allowChildrenMerge?: boolean): NavItem[] => {
    const mergeMapping = {} as Record<string, NavItem>
    const newNav = [] as NavItem[]
    for (const item of navItems) {
      if (!item) {
        continue
      }
      const path = item.path !== '#' && item.path
      const existing = (item.key && mergeMapping[item.key]) || (path && mergeMapping[path])
      const allowNavMerge = allowChildrenMerge || item.mergeNav
      if (!existing) { // new
        const newItem = { ...item } // make a copy for allow merge
        if (allowNavMerge) {
          newItem.mergeNav = allowNavMerge
        }
        if (item.key) {
          mergeMapping[item.key] = newItem
        }
        if (path) {
          mergeMapping[path] = newItem
        }
        if (_.isBoolean(newItem.avail)) {
          newItem.avail = undefined
        }
        newNav.push(newItem)
        continue
      } // else merge in
      if (!(allowNavMerge && existing.mergeNav) && !(existing.asPlaceholder || item.asPlaceholder)) { // not allow merge
        console.error('WARN: ignore duplicated nav item', item, 'found existing item with same key or path without mergeNav or asPlaceholder', existing)
        continue
      }
      if (existing.component && item.component && !existing.asPlaceholder) {
        console.error('WARN: ignore merge nav item with both existing and current has conflict component', item, 'existing', existing)
        continue
      }
      const { children, ...rest } = item // did a clone to avoid item is not extensible
      if (rest.asPlaceholder) { // placeholder after existing
        if (rest.component) {
          console.error('WARN: as placeholder, component is not allowed', item, 'existing', existing)
          continue
        }
        delete rest.id
        delete rest.appName
        delete rest.appVars
        delete rest.asPlaceholder
        delete rest.contentClassName
        Object.assign(rest, existing, mergeAvail(rest, existing), {
          children: mergeChildren(existing.children, children),
          _placeholder_applied: true,
        }) // merge to placeholder
        if (existing.asPlaceholder || existing._placeholder_applied) { // already has placeholder applied
          Object.assign(existing, rest)
          rest.asPlaceholder = true // remove the new one instead of existing
          continue
        }
        // then replace the existing mergeMapping
        if (path) {
          mergeMapping[path] = rest
        }
        if (item.key) {
          mergeMapping[rest.key] = rest
        }
        newNav.push(rest)
        existing.asPlaceholder = true // and mark the existing one to remove
        continue
      }
      // start merge
      Object.assign(existing, rest, mergeAvail(existing, rest), existing.component && existing.appName && {
        microAppId: existing.microAppId || existing.id || existing.appName,
        appName: existing.appName,
        appVars: existing.appVars,
        contentClassName: existing.contentClassName,
      }, {
        children: mergeChildren(existing.children, children)
      })
      if (existing.asPlaceholder) { // existing is placeholder
        existing._placeholder_applied = true
        existing.asPlaceholder = undefined // remove the flag
      }
    }
    const keyMap = {} as Record<string, NavItem>
    return newNav.filter(item => {
      if (item.asPlaceholder || !item.key) {
        return true
      }
      const existing = keyMap[item.key]
      if (existing) {
        console.error('found duplicated key after merge nav', item.key, existing, item)
        return false
      }
      keyMap[item.key] = item
      return true
    })
  }
  const cleanupPlaceholders = (children?: NavItem[]): NavItem[] => {
    const navList = [] as NavItem[]
    return children?.reduce((array, item) => {
      if (!item || item.asPlaceholder) {
        return array // filter out
      }
      if (!Object.isExtensible(item) || Object.isFrozen(item)) {
        item = { ...item } // do clone if item cannot be extended, and ignore non-iteratables
      }
      if (item.children) {
        item.children = cleanupPlaceholders(item.children)
      }
      if (item._placeholder_applied) {
        delete item._placeholder_applied // cleanup the internal flag
      }
      array.push(item)
      return array
    }, navList) || navList
  }

  __info('navigation before merge', microAppsNav)
  const nav = cleanupPlaceholders(mergeNav(ensureUseApprovedIcon(microAppsNav)))
  return nav.map(normalizeNav)
}
export const processActions = (navAppsResolved: InitedMicroApp[]) => {
  const microAppsActions = (navAppsResolved as InitedMicroAppConfig[]).reduce((allActions: NavAction[], app: InitedMicroAppConfig) => {
    if (!app.actions) {
      return allActions
    }
    const { appName, appVars } = app
    const actions = (_.isFunction(app.actions) && app.actions?.()) || app.actions
    const addName = (act: NavAction) => Object.assign(act, { appName, appVars })
    if (Array.isArray(actions)) {
      allActions.push(...actions.map(addName))
    }
    else {
      allActions.push(addName(actions))
    }
    return allActions.filter(action => {
      if (!action.key) {
        console.error('action key is required', action)
        return false
      }
      return true
    })
  }, [] as NavAction[])
  const actions = ensureUseApprovedIcon(microAppsActions.map(normalizeNav)) // .filter(action => !action.asPlaceholder)
  return actions as NavAction[]
}
export const processNav = (navAppsResolved: InitedMicroApp[]) => {
  const microAppsNav = processMicroApps(navAppsResolved) as NavItem[]
  const flattenNavigationList = getFlattenNavigationList(microAppsNav)
  const pathAvailMap = getPathAvailMap(flattenNavigationList)
  return Object.assign(microAppsNav, {
    pathAvailMap,
    flattenNavigationList,
  }) as NavigationCfg
}

export const processRoutes = (navAppsResolved: InitedMicroApp[]) => {
  const microAppsRoutes = (navAppsResolved as InitedMicroAppConfig[]).reduce((allRoutes: NavItem[], app: InitedMicroAppConfig) => {
    if (!app.routes) {
      return allRoutes
    }
    const { appName, appVars } = app
    const addName = (route: NavItem) => Object.assign(route, { appName, appVars })
    if (Array.isArray(app.routes)) {
      allRoutes.push(...app.routes.map(addName))
    }
    return allRoutes
  }, [] as NavItem[])
  const routes = microAppsRoutes.map(normalizeNav)
  __info('microAppRoutes ', microAppsRoutes)
  return routes
}

export const extractExposes = (exposes: Record<string, Exposes>, navAppsResolved: InitedMicroApp[]) => {
  const mergedExposes = { global: {} } as Record<string, Exposes>
  const nss = exposes && Object.keys(exposes)
  if (nss?.length) {
    for (const ns of nss) { // 2 level deep clone
      mergedExposes[ns] = { ...exposes[ns] }
    }
  }
  if (navAppsResolved?.length) {
    const __var_names__ = exposes.__var_names__ || {}
    for (const app of navAppsResolved as InitedMicroAppConfig[]) {
      mergeExposes(mergedExposes, app.exposes, app.appName, __var_names__[app.id || app.appName])
    }
  }
  __info('merged exposes', mergedExposes)
  return mergedExposes
}

export const generateNavActionExposes = (initedMicroApps: InitedMicroApp[]) => {
  const leftMainNavigation = processNav(initedMicroApps) as NavItemArray
  const SETTINGS_KEY = 'settings'
  const MSP_SETTINGS_KEY = 'msp-settings'
  const values = {
    leftMainNavigation,
    navRoutes: leftMainNavigation,
    microAppsInitPromise: undefined,
    leftMainActions: undefined,
    exposes: extractExposes(inited.settings.exposes, initedMicroApps),
  } as {
    initedMicroApps?: InitedMicroApp[]
    leftMainNavigation: NavItemArray
    navRoutes: (NavItem | NavAction)[]
    microAppsInitPromise?: Promise<any>
    leftMainActions?: NavAction[]
    leftMainRoutes?: NavItem[]
  }
  const actions = processActions(initedMicroApps)
  if (getFrameworkVar('use_new_left_nav')) { // TODO: refine it
    const cleanup = {
      hidden: undefined,
      avail: undefined,
      callAvail: undefined,
      isLoading: undefined,
      featureAvail: undefined,
      asPlaceholder: undefined,
    } as Partial<NavItem>
    const mergeSettings = (key: string, avail: AvailFunc) => {
      const settings = leftMainNavigation.find(item => item.key === key)
      if (!settings) {
        return
      }
      const placeholder = actions.find(item => item.key === key && item.asPlaceholder)
      const updateAction = {
        ...settings,
        ...cleanup,
        avail,
      } as NavAction
      if (placeholder) {
        Object.assign(placeholder, updateAction)
      }
      else {
        actions.unshift(updateAction)
      }
      settings.hidden = true
    }

    mergeSettings(SETTINGS_KEY, hasMicroApp(ASC_PORTAL_MICRO_APP) ?
      (({ access, location }: any) =>
        !location?.pathname?.startsWith(MSP_PATH_PREFIX) &&
        !access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)
      ) as AvailFunc : (({ location }: any) =>
        !location?.pathname?.startsWith(MSP_PATH_PREFIX)
      ) as AvailFunc
    )
    mergeSettings(MSP_SETTINGS_KEY, hasMicroApp(ASC_PORTAL_MICRO_APP) ?
      (({ access, location }: any) =>
        location?.pathname?.startsWith(MSP_PATH_PREFIX) &&
        !access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)
      ) as AvailFunc : (({ location }: any) =>
        location?.pathname?.startsWith(MSP_PATH_PREFIX)
      ) as AvailFunc
    )
    // _.remove(actions, item => item.asPlaceholder) // will remove before render
  }
  if (actions?.length) {
    values.leftMainActions = actions
    const hasRoute = (action: NavAction) => action.key && action.path && action.component
    const navActionRoutes = []
    for (const action of actions) {
      if (hasRoute(action)) {
        navActionRoutes.push(action)
      }
      else if (!action.component && isNonemptyArray(action.children)) {
        const routes = action.children.filter(hasRoute)
        if (routes.length) {
          navActionRoutes.push(...routes)
        }
      }
    }
    if (navActionRoutes.length) {
      values.navRoutes = leftMainNavigation.concat(navActionRoutes as NavItem[])
    }
  }
  const routes = processRoutes(initedMicroApps)
  if (routes?.length) {
    values.leftMainRoutes = routes
    const navRoutes = routes.filter((route: NavItem) =>
      route.key && route.path && route.component
    )
    if (navRoutes.length) {
      values.navRoutes.concat(navRoutes as NavItem[])
    }
  }
  return values
}
